'use client';

import ApiService, { type ApiResponse } from './api-services';

class DocumentServices extends ApiService {
  constructor() {
    super('/document');
  }

  /**
   * Get document information including URLs.
   *
   * Returns document metadata with generated URLs:
   * - PDF: Signed URL with 1-hour expiration (private storage)
   * - Legislation HTML: Public URL from IRI
   * - Decision HTML: secHtml and sudHtml public URLs
   *
   * Supports both:
   * - Numeric ID: getDocumentInfo(12345)
   * - IRI path: getDocumentInfo("SK/ZZ/1964/40")
   *
   * @param documentId - Document content ID (number) or IRI path (string)
   * @returns Promise with document data including urls field
   */
  getDocumentInfo(documentId: number | string) {
    return this.getData(`/${documentId}`);
  }

  /**
   * Get original_source URLs for multiple documents in batch.
   *
   * This endpoint returns a map of document IDs to their original slov-lex.sk URLs.
   * Optimized for batch operations (e.g., copying multiple document links).
   *
   * @param documentIds - Array of document content IDs
   * @returns Promise with map: { "123": "https://slov-lex.sk/...", "456": null, ... }
   *
   * @example
   * const result = await documentServices.getDocumentsOriginalSources([123, 456, 789]);
   * // result.data.data = { "123": "https://...", "456": null, "789": "https://..." }
   */
  getDocumentsOriginalSources(
    documentIds: number[]
  ): ApiResponse<{ data: Record<string, string | null> }> {
    return this.postData('/original-sources', { document_ids: documentIds });
  }

  /**
   * Resolve the correct legislation version document ID for a given effective date.
   *
   * This endpoint finds the legislation version that was effective on the specified date
   * and returns its document_content_id. Use this for "time travel" functionality
   * to view historical versions of legislation.
   *
   * @param documentId - The current document content ID (must be a legislation document)
   * @param ucinnost - The effective date in DD.MM.YYYY format (Slovak date format)
   * @returns Promise with resolved version data:
   *   - document_content_id: ID of the version effective on the date
   *   - iri: Full IRI of the resolved version
   *   - is_same: Boolean indicating if resolved version equals the requested document
   *   - effective_from: Start date of the resolved version (DD.MM.YYYY)
   *   - effective_till: End date of the resolved version or null (DD.MM.YYYY)
   *
   * @example
   * const result = await documentServices.resolveLegislationVersion(4536645, '10.01.2025');
   * if (!result.data.data.is_same) {
   *   // Redirect to the resolved version
   *   router.push(`/document/${result.data.data.document_content_id}?ucinnost=10.01.2025`);
   * }
   */
  resolveLegislationVersion(
    documentId: number,
    ucinnost: string
  ): ApiResponse<{
    data: {
      document_content_id: number;
      iri: string;
      is_same: boolean;
      effective_from: string | null;
      effective_till: string | null;
    };
  }> {
    return this.getData(
      `/${documentId}/resolve-version?ucinnost=${encodeURIComponent(ucinnost)}`
    );
  }
}

export default new DocumentServices();
